import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /** Success feedback is called only after Shopify confirms the cart addition. */
  showSuccess() {
    const button = this.refs.addToCartButton;
    if (button.dataset.puppet !== 'true' && this.dataset.addToCartAnimation === 'true' && !this.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
    this.animateAddToCart();
  }

  // The form submit handler owns validation and pending state; a click is not a success.
  handleClick() {}

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  #variantChangeFailed = false;
  #variantAvailable = true;
  #addInProgress = false;

  /** Whether feedback can safely describe the currently displayed selection. */
  get variantSelectionReady() {
    return !this.#variantChangeInProgress && !this.#variantChangeFailed;
  }

  connectedCallback() {
    super.connectedCallback();

    this.#abortController.abort();
    this.#abortController = new AbortController();
    if (!this.dataset.purchaseState) this.#variantAvailable = !this.querySelector('[ref="addToCartButton"]')?.disabled;
    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    target?.addEventListener('variant:error', this.#onVariantError, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    if (this.#timeout) clearTimeout(this.#timeout);
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      const response = await fetch('/cart.js', { signal: controller.signal });
      if (!response.ok) throw new Error('Unable to refresh the cart');
      const cart = await response.json();
      if (!Array.isArray(cart.items)) throw new Error('Invalid cart response');

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    } finally {
      clearTimeout(timeout);
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();
    // Never queue purchase intent against an unresolved variant or repeat an in-flight POST.
    if (this.#variantChangeInProgress || this.#variantChangeFailed || this.#addInProgress) return;
    return this.#processAddToCart(event);
  }

  #syncPurchaseState() {
    const busy = this.#addInProgress || this.#variantChangeInProgress;
    const blocked = busy || this.#variantChangeFailed || !this.#variantAvailable;
    const label = this.#addInProgress
      ? this.dataset.addingLabel || 'Adding…'
      : this.dataset.variantLoadingLabel || 'Updating your selection…';
    if (busy) this.setAttribute('aria-busy', 'true');
    else this.removeAttribute('aria-busy');
    this.dataset.purchaseState = this.#addInProgress ? 'adding' : this.#variantChangeInProgress ? 'selecting' : this.#variantChangeFailed ? 'selection-error' : 'ready';
    for (const container of this.querySelectorAll('add-to-cart-component')) {
      const button = container.refs.addToCartButton;
      button.disabled = blocked;
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
      const text = button.querySelector('.add-to-cart-text__content > span > span') || button.querySelector('.add-to-cart-text__content');
      if (!text) continue;
      if (busy) {
        text.dataset.purchaseOriginalLabel ??= text.textContent;
        text.textContent = label;
      } else if (text.dataset.purchaseOriginalLabel !== undefined) {
        text.textContent = text.dataset.purchaseOriginalLabel;
        delete text.dataset.purchaseOriginalLabel;
      }
    }
    this.refs.acceleratedCheckoutButtonContainer?.toggleAttribute('hidden', blocked);
    this.dispatchEvent(new CustomEvent('purchase:state', {
      bubbles: true,
      detail: { productId: this.dataset.productId, busy, blocked, label },
    }));
  }

  #clearError() {
    if (this.#timeout) clearTimeout(this.#timeout);
    // A separate cart request may finish while a newer selection still needs recovery.
    if (this.#variantChangeFailed) return;
    this.refs.addToCartTextError?.classList.add('hidden');
  }

  /** @param {string} message @param {boolean} [retrySelection] */
  #showError(message, retrySelection = false) {
    if (this.#timeout) clearTimeout(this.#timeout);
    let error = this.refs.addToCartTextError;
    if (!error) {
      error = document.createElement('span');
      error.className = 'product-form-text__error';
      error.setAttribute('ref', 'addToCartTextError');
      this.querySelector('form')?.prepend(error);
    }
    error.classList.remove('hidden');
    error.replaceChildren(document.createTextNode(message));
    if (retrySelection) {
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'button-unstyled';
      retry.style.textDecoration = 'underline';
      retry.textContent = this.dataset.retrySelectionLabel || 'Retry selection';
      retry.addEventListener('click', () => {
        const scope = this.closest('.shopify-section, dialog, product-card');
        scope?.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`)?.retrySelection?.();
      });
      error.append(document.createTextNode(' '), retry);
    }
    this.#setLiveRegionText(message);
  }

  /** @param {Event} event */
  async #processAddToCart(event) {
    const form = this.querySelector('form');
    if (!form || !form.checkValidity() || !this.#variantAvailable) return;
    const containers = [...this.querySelectorAll('add-to-cart-component')];
    if (containers.some((container) => container.refs.addToCartButton?.disabled)) return;
    const validation = this.refs.quantitySelector?.canAddToCart?.();
    if (validation && !validation.canAdd) {
      const message = (this.dataset.quantityErrorMax || '').replace('{{ maximum }}', String(validation.maxQuantity ?? ''));
      this.#showError(message);
      this.dispatchEvent(new CartErrorEvent(form.id || '', message, message, {}));
      return;
    }

    const formData = new FormData(form);
    const variantId = formData.get('id');
    if (!variantId) return;
    const quantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .filter((item) => item instanceof HTMLElement && item.dataset.sectionId)
      .map((item) => item.dataset.sectionId);
    formData.set('sections', sectionIds.join(','));

    this.#clearError();
    this.#addInProgress = true;
    this.#syncPurchaseState();
    this.#setLiveRegionText(this.dataset.addingLabel || 'Adding…');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const config = fetchConfig('javascript', { body: formData });
      const result = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'application/json' },
        signal: controller.signal,
      });
      const response = await result.json();
      // Shopify may partially add stock on a business-rule error; refresh the cart without success feedback.
      if (response.status) {
        const message = typeof response.description === 'string' ? response.description : response.message;
        this.#showError(message, this.#variantChangeFailed);
        this.dispatchEvent(new CartErrorEvent(form.id || '', response.message, response.description, response.errors));
        this.dispatchEvent(new CartAddEvent({}, this.id, {
          didError: true, source: 'product-form-component', itemCount: quantity, productId: this.dataset.productId,
        }));
        return;
      }
      // Apps and bundles can return an items array or transform the submitted parent variant.
      const addedItems = Array.isArray(response.items) ? response.items : [response];
      if (!result.ok || !addedItems.length || !addedItems.every((item) => item?.id && Number(item.quantity) > 0)) {
        throw new Error('The cart addition was not confirmed');
      }

      this.#clearError();
      if (!this.#variantChangeInProgress && !this.#variantChangeFailed && String(this.refs.variantId.value) === String(variantId)) {
        for (const container of this.querySelectorAll('add-to-cart-component')) container.showSuccess?.();
      }
      if (!this.#variantChangeFailed) {
        this.#setLiveRegionText(Theme.translations.added);
        this.#timeout = setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
      }
      const cart = await this.#fetchAndUpdateCartQuantity();
      this.dispatchEvent(new CartAddEvent(cart ?? undefined, String(variantId), {
        source: 'product-form-component', itemCount: quantity, productId: this.dataset.productId,
        sections: response.sections,
      }));
    } catch (error) {
      // A lost response does not prove the POST failed. Do not retry automatically or claim the cart is unchanged.
      const message = this.dataset.cartErrorLabel || 'We couldn’t confirm your basket update. Check your basket before trying again.';
      this.#showError(message, this.#variantChangeFailed);
      this.dispatchEvent(new CartErrorEvent(form.id || '', message, message, {}));
      console.error('Cart update could not be confirmed', error);
    } finally {
      clearTimeout(timeout);
      this.#addInProgress = false;
      this.#syncPurchaseState();
      cartPerformance.measureFromEvent('add:user-action', event);
    }
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    this.#variantChangeInProgress = false;
    this.#variantChangeFailed = false;
    this.#variantAvailable = Boolean(event.detail.resource?.available);
    this.#clearError();

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    this.#syncPurchaseState();
    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} event */
  #onVariantSelected = (event) => {
    if (event.target?.dataset.productId !== this.dataset.productId) return;
    this.#variantChangeInProgress = true;
    this.#variantChangeFailed = false;
    this.#clearError();
    this.#syncPurchaseState();
  };

  #onVariantError = (event) => {
    if (event.detail?.productId !== this.dataset.productId) return;
    this.#variantChangeInProgress = false;
    this.#variantChangeFailed = true;
    this.#syncPurchaseState();
    this.#showError(this.dataset.variantErrorLabel || 'We couldn’t update your selection. Please try again.', true);
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
